const moment = require('moment');
const {
  addendumFieldsComplete,
  addendumFieldValues,
  addendumRequiresSignature
} = require('./contract-addendum');

// Development copy pending legal review.
const ESIGN_CONSENT_VERSION = '2.0-draft, pending legal review';
const ESIGN_CONSENT_TEXT =
  'I agree to use electronic records and signatures for this Rental Agreement and its addenda. I can access and save or print these documents, and I intend my electronic signature to sign this agreement.';
const ESIGN_DISCLOSURE_TITLE = 'Electronic Records and Signatures Disclosure';
const ESIGN_DISCLOSURE_FACT =
  "Consent applies to this reservation's Rental Agreement and its addenda.";
const ESIGN_DISCLOSURE_SECTIONS = [
  'Requesting paper copies',
  'Withdrawing consent before signing',
  'Updating contact information',
  'Fees or consequences',
  'Device/browser requirements for accessing and retaining documents'
];
const signatureRequired = contract =>
  Boolean(
    contract.e_sig_required ||
      addendumRequiresSignature(contract.addendum_fields)
  );
const photoRequired = contract =>
  Boolean(contract.photo_id_required && !contract.id_photo_on_file);
const contractSigningState = (contract, result) => {
  if (
    (result && result.success === true) ||
    (contract && contract.already_signed === true)
  )
    return 'signed';
  return contract ? 'form' : 'loading';
};
const buildContractSignPayload = (
  contract,
  { consent, signature, photo, values = {} }
) => {
  if (consent !== true)
    throw new Error('Please agree to electronic records and signatures.');
  if (signatureRequired(contract) && !signature)
    throw new Error('Please save your signature.');
  if (signature && !/^data:image\//.test(signature))
    throw new Error('Please draw and save your signature again.');
  if (photoRequired(contract) && !photo)
    throw new Error('Please upload a photo ID.');
  if (!addendumFieldsComplete(contract.addendum_fields, values, signature))
    throw new Error('Please complete all addendum fields.');
  return {
    booking_code: contract.booking_code,
    esign_consent: true,
    esign_consent_text: `${ESIGN_CONSENT_TEXT} (v${ESIGN_CONSENT_VERSION})`,
    field_values: addendumFieldValues(contract.addendum_fields, values),
    ...(signature ? { signature_data: signature } : {}),
    ...(photo ? { id_photo_data: photo } : {})
  };
};
const alreadySignedError = error => {
  const data = error && error.response && error.response.data;
  const message =
    typeof data === 'string'
      ? data
      : data &&
        (data.message || (data.error && data.error.message) || data.error);
  return /already (?:been )?signed/i.test(
    String(message || (error && error.message) || '')
  );
};
const signFailureMessage = error => {
  const response = error && error.response;
  const data = response && response.data;
  const message =
    typeof data === 'string'
      ? data
      : data &&
        (data.message || (data.error && data.error.message) || data.error);
  return response &&
    response.status >= 400 &&
    response.status < 500 &&
    typeof message === 'string' &&
    message.trim()
    ? message
    : 'Unable to sign the agreement. Please try again.';
};
const formatContractTimestamp = value =>
  value && moment(value, moment.ISO_8601, true).isValid()
    ? moment(value, moment.ISO_8601, true).format('MMM D, YYYY h:mm A')
    : null;
const verificationNavigationUrl = (value, origin) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value, origin);
    return url.protocol === 'https:' ||
      (url.protocol === 'http:' && url.origin === origin)
      ? url.href
      : null;
  } catch (error) {
    return null;
  }
};
const renderContractTerms = (html, contract) => {
  const keys = [
    'guest_name',
    'booking_code',
    'property_name',
    'unit_name',
    'check_in',
    'check_out',
    'num_guests',
    'brand_name'
  ];
  return String(html || '').replace(/{{\s*(\w+)\s*}}/g, (token, key) => {
    if (!keys.includes(key)) return token;
    let value = contract[key];
    if (['check_in', 'check_out'].includes(key) && value) {
      value = moment(value).isValid()
        ? moment(value).format('MMM D, YYYY')
        : '';
    }
    return String(value == null ? '' : value).replace(
      /[&<>"']/g,
      character =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        }[character])
    );
  });
};
module.exports = {
  verificationNavigationUrl,
  signFailureMessage,
  formatContractTimestamp,
  renderContractTerms,
  ESIGN_CONSENT_TEXT,
  ESIGN_CONSENT_VERSION,
  ESIGN_DISCLOSURE_TITLE,
  ESIGN_DISCLOSURE_FACT,
  ESIGN_DISCLOSURE_SECTIONS,
  signatureRequired,
  photoRequired,
  contractSigningState,
  buildContractSignPayload,
  alreadySignedError
};
